import React, { MouseEvent, useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { useSpring } from 'react-spring';
import { useDispatch, useSelector } from 'react-redux';

import { IState } from 'store';
import { cleanCartData, ICartProps } from 'store/modules/cart';

import ProductItemMiniCart from './ProductItemMiniCart';

import * as S from './styles';
import { useNotices } from 'hooks';
import { getGACartItemsParams } from 'utils/gtag';

interface IMiniCartProps {
  onClose(): void;
}

const MiniCart: React.FC<IMiniCartProps> = ({ onClose }) => {
  const { push } = useRouter();
  const dispatch = useDispatch();
  const { addNotices } = useNotices();

  const cart = useSelector<IState, ICartProps>(state => state.cart.cart);

  const [reverse, setReverse] = useState(false);

  const miniCartTransition = useSpring({
    from: { right: '-120%', opacity: 0 },
    to: { right: '0%', opacity: 1 },
    reset: reverse,
    reverse,
  });

  const handleCloseModal = useCallback(() => {
    setReverse(state => !state);

    setTimeout(() => {
      onClose();
    }, 500);
  }, [onClose]);

  const handleControlPropagation = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
    },
    [],
  );

  const handleGoToCart = useCallback(() => {
    handleCloseModal();
    push('/checkout/carrinho');
  }, [push, handleCloseModal]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';

    return () => {
      document.body.style.overflow = 'auto';
      document.body.style.height = 'initial';
    };
  }, []);

  const handleCleanCart = useCallback(() => {
    addNotices({
      messages: ['Tem certeza que deseja remover todos os itens da cesta?'],
      type: 'warn',
      needConfirmation: true,
      action: () =>
        dispatch(
          cleanCartData({
            GA: getGACartItemsParams({
              product: cart.itens,
              qty: 0,
              remove: true,
            }),
          }),
        ),
    });
  }, [addNotices, dispatch, cart]);

  return (
    <S.ContainerWrapper
      onClick={onClose}
      role="presentation"
      className="mini-cart-wrapper"
    >
      <S.Container
        style={miniCartTransition}
        onClick={handleControlPropagation}
        className="minicart"
      >
        {!cart.estaVazio && (
          <span className="button-clean-cart" onClick={handleCleanCart}>
            Limpar cesta
          </span>
        )}

        <div className="mini-cart-header">
          <div>
            <img
              src="/images/icons/basket-black.svg"
              alt=""
              className="icon shopping-blue-icon"
            />

            <h3>Minha cesta</h3>
          </div>

          <img
            src="/images/icons/x-blue.svg"
            alt=""
            className="icon x-icon"
            onClick={handleCloseModal}
          />
        </div>

        <div className="products-list-mini-cart">
          {cart.itens.map(item => (
            <ProductItemMiniCart key={item.codigo} item={item} />
          ))}
        </div>

        <div className="subtotal-value-and-buttons">
          <div className="subtotal-value">
            <span>Subtotal:</span>
            <strong>{cart.totalSemFreteTexto}</strong>
          </div>

          <div className="group-buttons">
            <button
              className="mini-cart-button outline"
              onClick={handleGoToCart}
            >
              Ver cesta completa
            </button>
            <button onClick={handleCloseModal} className="mini-cart-button">
              Continuar comprando
            </button>
          </div>
        </div>
      </S.Container>
    </S.ContainerWrapper>
  );
};

export default MiniCart;
